import type {
  Paginated,
  UpdateUserPayload,
  UserListItem,
} from '@atlas/api-contract';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '@/lib/api/errors';
import { listUsers, updateUser } from '@/lib/api/users';
import { queryKeys } from '@/lib/query/keys';

import { usersToQuery, type UsersSearch } from './users-search';

/**
 * React Query bindings for the admin Users screen (spec R7.7).
 *
 * The update is not optimistic: a role change or a deactivation can be refused
 * by the API (self-role-change, last-admin), so the answer is awaited and the
 * list invalidated only once it lands — showing a change the server then
 * rejected would be worse than a moment's wait.
 */

export const usersQueryOptions = (search: UsersSearch) => {
  const query = usersToQuery(search);

  return queryOptions<Paginated<UserListItem>, ApiError>({
    queryKey: queryKeys.users.list(query),
    queryFn: ({ signal }) => listUsers(query, { signal }),
    /* Keep the current page visible, dimmed, while the next one loads. */
    placeholderData: keepPreviousData,
  });
};

export const useUsersQuery = (
  search: UsersSearch,
): UseQueryResult<Paginated<UserListItem>, ApiError> =>
  useQuery(usersQueryOptions(search));

export interface UpdateUserVariables {
  userId: string;
  payload: UpdateUserPayload;
}

export const useUpdateUser = (): UseMutationResult<
  UserListItem,
  ApiError,
  UpdateUserVariables
> => {
  const queryClient = useQueryClient();

  return useMutation<UserListItem, ApiError, UpdateUserVariables>({
    mutationFn: ({ userId, payload }) => updateUser(userId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() }),
  });
};
